/**
 * Pipeline Builder Modal — 7단계 지식 파이프라인 위저드 (오케스트레이터)
 *
 * 상태 관리와 단계 흐름만 담당합니다.
 * 각 단계의 UI는 steps/ 디렉토리의 개별 컴포넌트가 처리합니다.
 *
 * Step 1: SourceSelectStep    — 소스 선택 (파일 / 웹 / DB)
 * Step 2: FileSourceStep      — 파일 업로드
 *         WebSourceStep       — 웹 크롤링 설정    (복합 소스 탭으로 전환)
 *         DatabaseSourceStep  — DB 커넥터 설정
 * Step 3: PreprocessingStep   — 전처리 (소스별 탭)
 * Step 4: ChunkingStep        — 청킹 전략 (소스별 탭)
 * Step 5: EmbeddingStep       — 임베딩 모델 선택 (공통)
 * Step 6: SearchConfigStep    — 검색 설정 (공통)
 * Step 7: ReviewStep          — 설정 확인
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Badge, Button, Modal, Tooltip, useToast } from '../ui';
import {
  knowledgeBaseApi,
  authApi,
  KnowledgeBase,
  ChunkingConfig,
  PreprocessingConfig,
  SearchConfig,
  EMBEDDING_MODELS,
} from '../../api';
import connectorApi from '../../api/connectors';
import {
  SourceType,
  WebConfig,
  DbConfig,
  ParserConfig,
  SOURCE_LABELS,
  DEFAULT_WEB_PREPROCESSING,
  DEFAULT_DB_PREPROCESSING,
  DEFAULT_CHUNKING,
  DEFAULT_WEB_CHUNKING,
  DEFAULT_WEB_CONFIG,
  DEFAULT_DB_CONFIG,
  DEFAULT_PARSER_CONFIG,
} from './shared';
import {
  SourceSelectStep,
  DataSourceStep,
  PreprocessingStep,
  ChunkingStep,
  EmbeddingStep,
  SearchConfigStep,
  ReviewStep,
  validateChunking,
} from './steps';
import '../../styles/kbPipeline.css';
import GlobeIcon from '../../assets/icons/pipeline/step-globe.svg?react';
import LinkIcon from '../../assets/icons/pipeline/step-link.svg?react';
import FilterIcon from '../../assets/icons/pipeline/step-filter.svg?react';
import BlocksIcon from '../../assets/icons/pipeline/step-blocks.svg?react';
import CubeIcon from '../../assets/icons/pipeline/step-cube.svg?react';
import SearchIcon from '../../assets/icons/pipeline/step-search.svg?react';
import FlagIcon from '../../assets/icons/pipeline/step-flag.svg?react';
import CheckIcon from '../../assets/icons/pipeline/step-check.svg?react';
import InfoIcon from '../../assets/icons/common/info.svg?react';

// ── 스텝 정의 ──
const STEPS = [
  { num: 1, label: '소스 선택', icon: GlobeIcon },
  { num: 2, label: '데이터 소스', icon: LinkIcon },
  { num: 3, label: '전처리', icon: FilterIcon },
  { num: 4, label: '청킹', icon: BlocksIcon },
  { num: 5, label: '임베딩', icon: CubeIcon },
  { num: 6, label: '검색 설정', icon: SearchIcon },
  { num: 7, label: '확인', icon: FlagIcon },
];


// 단계별 안내 문구 - 헤더 제목 옆 (i) 툴팁으로 노출
const HINT_TEXTS = [
  '',
  '지식베이스 이름을 입력하고 데이터 소스를 선택하세요.',
  '각 소스 탭을 클릭하여 데이터 소스 정보를 입력하세요.',
  '소스 탭별로 전처리 옵션을 설정하세요.',
  '소스 탭별로 청킹 전략을 설정하세요.',
  '텍스트를 벡터로 변환할 임베딩 모델을 선택하세요.',
  '검색 시 사용할 파라미터를 조정하세요.',
  '설정을 확인하고 "게시하기"를 눌러 지식베이스를 생성하세요.',
];

const DEFAULT_SEARCH: SearchConfig = {
  topK: 7,
  similarityThreshold: 0.5,
  enableReranking: false,
};

// ── Props ──
interface Props {
  knowledgeBase: KnowledgeBase | null;
  /**
   * 단일 소스 페이지에서 열릴 때 해당 소스를 지정합니다.
   * - 지정하면 해당 소스만 선택·표시됩니다 (나머지 숨김).
   * - undefined이면 복합 소스 모드로 모든 소스 선택 가능합니다.
   */
  defaultSourceType?: SourceType;
  mode?: 'admin' | 'user';
  onClose: () => void;
  onComplete: () => void;
}

export function PipelineBuilderModal({
  knowledgeBase,
  defaultSourceType,
  mode = 'admin',
  onClose,
  onComplete,
}: Props) {
  const { showToast } = useToast();
  const modalRef = useRef<HTMLDivElement>(null);
  const isUserMode = mode === 'user';

  // ── 단계 ──
  const [currentStep, setCurrentStep] = useState(1);

  // ── 기본 정보 ──
  const [kbName, setKbName] = useState(knowledgeBase?.name ?? '');
  const [kbDescription, setKbDescription] = useState(knowledgeBase?.description ?? '');
  const [kbCustomerId, setKbCustomerId] = useState('');
  const [kbTeamId, setKbTeamId] = useState('');

  // 팀 선택 필요 여부 (상위 관리자 역할)
  const needsTeamSelectInModal = () => {
    const role = authApi.getUser()?.role || '';
    return ['productAdmin', 'tenantAdmin', 'customerAdmin', 'admin'].includes(role);
  };
  const [kbId, setKbId] = useState(knowledgeBase?.id ?? '');
  const [uploading, setUploading] = useState(false);

  // ── 소스 선택 ──
  // defaultSourceType이 있으면 해당 소스만 활성화 (단일 소스 페이지)
  // undefined이면 복합 소스 모드 — 모든 소스 선택 가능
  const isSingleSourcePage = !isUserMode && !!defaultSourceType;

  const [hasFile, setHasFile] = useState(
    isUserMode || defaultSourceType === 'file',
  );
  const [hasWeb, setHasWeb] = useState(
    !isUserMode && defaultSourceType === 'web',
  );
  const [hasDatabase, setHasDatabase] = useState(
    !isUserMode && defaultSourceType === 'database',
  );

  const activeSources: SourceType[] = [
    ...(hasFile ? ['file' as const] : []),
    ...(hasWeb ? ['web' as const] : []),
    ...(hasDatabase ? ['database' as const] : []),
  ];

  // ── 공유 탭 상태 (Step 2/3/4에서 공통 사용) ──
  // 복합 소스 모드면 'file'을 기본 탭으로, 단일 소스면 해당 소스 탭
  const [activeTab, setActiveTab] = useState<SourceType>(defaultSourceType ?? 'file');

  // 소스 선택 변경 시 첫 번째 활성 탭으로 이동
  useEffect(() => {
    if (activeSources.length > 0 && !activeSources.includes(activeTab)) {
      setActiveTab(activeSources[0]);
    }
  }, [hasFile, hasWeb, hasDatabase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 파일 소스 상태 ──
  const [files, setFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  // Google Drive에서 선택한 파일
  const [driveFiles, setDriveFiles] = useState<{ fileId: string; fileName: string }[]>([]);
  // Dropbox에서 선택한 파일
  const [dropboxFiles, setDropboxFiles] = useState<{ filePath: string; fileName: string }[]>([]);

  // ── 웹 소스 상태 ──
  const [webConfig, setWebConfig] = useState<WebConfig>(DEFAULT_WEB_CONFIG);

  // ── DB 소스 상태 ──
  const [dbConfig, setDbConfig] = useState<DbConfig>(() => {
    // 편집 모드: 기존 pipelineConfig에서 DB 설정 복원
    if (knowledgeBase) {
      const cfg = knowledgeBase.pipelineConfig as any;
      const dbSrc = cfg?.sources?.find((s: any) => s.type === 'database') || cfg?.dbConfig;
      if (dbSrc) {
        return {
          type: dbSrc.dbType || dbSrc.type || 'postgresql',
          host: dbSrc.host || '',
          port: dbSrc.port || 5432,
          database: dbSrc.database || '',
          schema: dbSrc.schema || '',
          username: dbSrc.username || '',
          password: dbSrc.password || '',
          syncSchedule: dbSrc.syncSchedule || 'manual',
          tables: (dbSrc.tables || []).map((t: any) => ({
            schema: t.schema,
            table: t.table,
            selected: t.selected !== false,
            labelGenerated: t.labelGenerated || false,
            columns: (t.columns || []).map((c: any) => ({
              name: c.name,
              type: c.type,
              nullable: c.nullable ?? true,
              comment: c.comment || '',
              label: c.label || '',
              include: c.include !== false,
            })),
          })),
          mode: dbSrc.mode ?? 'text2sql',
          sampleRows: dbSrc.sampleRows ?? 3,
          hints: dbSrc.hints ?? [],
          sqlSystemPrompt: dbSrc.sqlSystemPrompt ?? undefined,
          sqlSummaryPrompt: dbSrc.sqlSummaryPrompt ?? undefined,
        };
      }
    }
    return DEFAULT_DB_CONFIG;
  });

  // ── 전처리 상태 (소스별) ──
  // 파일 소스는 파서 선택 방식, 웹/DB는 기존 체크박스 방식
  const [fileParser, setFileParser] = useState<ParserConfig>(DEFAULT_PARSER_CONFIG);
  const [webPreprocessing, setWebPreprocessing] = useState<PreprocessingConfig>(DEFAULT_WEB_PREPROCESSING);
  const [dbPreprocessing, setDbPreprocessing] = useState<PreprocessingConfig>(DEFAULT_DB_PREPROCESSING);

  // ── 청킹 상태 (소스별) ──
  const [fileChunking, setFileChunking] = useState<ChunkingConfig>(DEFAULT_CHUNKING);
  const [webChunking, setWebChunking] = useState<ChunkingConfig>(DEFAULT_WEB_CHUNKING);
  const [dbChunking, setDbChunking] = useState<ChunkingConfig>({ ...DEFAULT_CHUNKING, strategy: 'fixed', chunkOverlap: 0 });

  // ── 임베딩 / 검색 (공통) ──
  const [embeddingModelId, setEmbeddingModelId] = useState(EMBEDDING_MODELS[0].id);
  // Cohere 멀티모달 여부: 'text'(텍스트 임베딩) | 'multimodal'(이미지 직접 임베딩)
  const [embeddingMode, setEmbeddingMode] = useState<'text' | 'multimodal'>('text');
  const [search, setSearch] = useState<SearchConfig>(DEFAULT_SEARCH);

  const isEditing = !!knowledgeBase;

  // ── 모달 드래그 이동 ──
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('button')) return;
      setIsDragging(true);
      dragOffset.current = { x: e.clientX - position.x, y: e.clientY - position.y };
      e.preventDefault();
    },
    [position],
  );

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isDragging) return;
      setPosition({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
    };
    const onUp = () => setIsDragging(false);
    if (isDragging) {
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDragging]);

  // ── 네비게이션 유효성 검사 ──
  const canProceed = () => {
    if (currentStep === 1) {
      const teamRequired = needsTeamSelectInModal();
      return !!kbName.trim() && activeSources.length > 0 && (!teamRequired || !!kbTeamId);
    }
    if (currentStep === 2) {
      if (hasDatabase) {
        // 연결 테스트 후 테이블이 1개 이상 선택돼야 다음 가능
        const hasSelectedTable = dbConfig.tables.some(t => t.selected);
        return hasSelectedTable;
      }
    }
    if (currentStep === 4) {
      // 모든 활성 소스의 청킹 유효성 검사
      const chunkingMap: Record<SourceType, ChunkingConfig> = {
        file: fileChunking,
        web: webChunking,
        database: dbChunking,
      };
      for (const source of activeSources) {
        const { isValid } = validateChunking(chunkingMap[source], source);
        if (!isValid) return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (!kbName.trim()) { showToast('error', '지식베이스 이름을 입력해주세요.'); return; }
      if (activeSources.length === 0) { showToast('error', '데이터 소스를 하나 이상 선택해주세요.'); return; }
      if (needsTeamSelectInModal() && !kbTeamId) { showToast('error', '팀을 선택해주세요.'); return; }
    }
    if (currentStep === 2) {
      if (hasFile && files.length === 0 && driveFiles.length === 0 && dropboxFiles.length === 0 && !isEditing) {
        showToast('error', '파일을 하나 이상 추가해주세요.'); return;
      }
      if (hasWeb && !webConfig.url) { showToast('error', '웹 크롤링 URL을 입력해주세요.'); return; }
      if (hasDatabase && (!dbConfig.host || !dbConfig.database || !dbConfig.username)) {
        showToast('error', 'DB 연결 정보를 모두 입력해주세요.'); return;
      }
      if (hasDatabase && !dbConfig.tables.some(t => t.selected)) {
        showToast('error', '테이블을 1개 이상 선택해주세요.'); return;
      }
    }
    if (currentStep === 4) {
      // 청킹 유효성 검사 실패 시 토스트
      const chunkingMap: Record<SourceType, ChunkingConfig> = {
        file: fileChunking,
        web: webChunking,
        database: dbChunking,
      };
      for (const source of activeSources) {
        const { isValid } = validateChunking(chunkingMap[source], source);
        if (!isValid) {
          showToast('error', '청킹 설정 값이 유효하지 않습니다. 범위를 확인해주세요.');
          return;
        }
      }
    }
    if (currentStep < 7) setCurrentStep((s) => s + 1);
  };

  const handlePrev = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  // ── 게시 ──
  const handlePublish = async () => {
    try {
      setUploading(true);

      const sources: any[] = [];
      if (hasFile) sources.push({ type: 'file', parser: fileParser, chunking: fileChunking });
      if (hasWeb) sources.push({ type: 'web', ...webConfig, preprocessing: webPreprocessing, chunking: webChunking });
      if (hasDatabase) sources.push({
        type: 'database',
        dbType: dbConfig.type,
        host: dbConfig.host,
        port: dbConfig.port,
        username: dbConfig.username,
        password: dbConfig.password,
        database: dbConfig.database,
        schema: dbConfig.schema,
        syncSchedule: dbConfig.syncSchedule,
        mode: dbConfig.mode,
        sampleRows: dbConfig.sampleRows,
        hints: (dbConfig.hints ?? []).filter((h: any) => h.sql.trim()),
        sqlSystemPrompt: dbConfig.sqlSystemPrompt || undefined,
        sqlSummaryPrompt: dbConfig.sqlSummaryPrompt || undefined,
        // 선택된 테이블은 컬럼 정보 전체 저장, 미선택 테이블은 식별자만 저장 (DynamoDB 400KB 제한)
        tables: dbConfig.tables
          .map(t => ({
            schema: t.schema,
            table: t.table,
            selected: t.selected,
            labelGenerated: t.labelGenerated,
            // 선택된 테이블만 컬럼 상세 저장, 미선택은 빈 배열
            columns: t.selected ? t.columns.map(c => ({
              name: c.name,
              type: c.type,
              nullable: c.nullable,
              comment: c.comment,
              label: c.label,
              include: c.include,
            })) : [],
          })),
        preprocessing: dbPreprocessing,
        chunking: dbChunking,
      });

      const primarySourceType = hasFile ? 'file' : hasWeb ? 'web' : 'database';
      let targetKbId = kbId;

      if (!targetKbId) {
        const primaryChunking = hasFile ? fileChunking : hasWeb ? webChunking : dbChunking;
        const kb = await knowledgeBaseApi.create({
          name: kbName,
          description: kbDescription || undefined,
          ...(kbCustomerId ? { customerIdOverride: kbCustomerId } : {}),
          ...(kbTeamId ? { teamIdOverride: kbTeamId } : {}),
          sourceType: primarySourceType,
          pipelineConfig: {
            sourceType: primarySourceType,
            sources,
            chunking: primaryChunking,
            embeddingModelId,
            embeddingMode,
            search,
            ...(hasFile && { fileParser }),
            ...(hasWeb && { webConfig }),
            ...(hasDatabase && {
              dbConfig: {
                type: dbConfig.type,
                host: dbConfig.host,
                port: dbConfig.port,
                database: dbConfig.database,
                schema: dbConfig.schema || undefined,
                username: dbConfig.username,
                password: dbConfig.password,
                syncSchedule: dbConfig.syncSchedule,
                tables: dbConfig.tables.map(t => ({
                  schema: t.schema,
                  table: t.table,
                  selected: t.selected,
                  labelGenerated: t.labelGenerated,
                  columns: t.selected ? t.columns.map(c => ({
                    name: c.name, type: c.type, nullable: c.nullable,
                    comment: c.comment, label: c.label, include: c.include,
                  })) : [],
                })),
              },
            }),
          } as any,
        });
        targetKbId = kb.id;
        setKbId(kb.id);
      } else {
        // 편집 모드 — pipelineConfig 업데이트
        const primaryChunking = hasFile ? fileChunking : hasWeb ? webChunking : dbChunking;
        await knowledgeBaseApi.update(targetKbId, {
          name: kbName,
          description: kbDescription || undefined,
          pipelineConfig: {
            sourceType: primarySourceType,
            sources,
            chunking: primaryChunking,
            embeddingModelId,
            embeddingMode,
            search,
            ...(hasFile && { fileParser }),
            ...(hasWeb && { webConfig }),
            ...(hasDatabase && {
              dbConfig: {
                type: dbConfig.type,
                host: dbConfig.host,
                port: dbConfig.port,
                database: dbConfig.database,
                schema: dbConfig.schema || undefined,
                username: dbConfig.username,
                password: dbConfig.password,
                syncSchedule: dbConfig.syncSchedule,
                tables: dbConfig.tables.map(t => ({
                  schema: t.schema,
                  table: t.table,
                  selected: t.selected,
                  labelGenerated: t.labelGenerated,
                  columns: t.selected ? t.columns.map(c => ({
                    name: c.name, type: c.type, nullable: c.nullable,
                    comment: c.comment, label: c.label, include: c.include,
                  })) : [],
                })),
              },
            }),
          } as any,
        });
      }

      if (hasFile && files.length > 0) {
        for (const file of files) await knowledgeBaseApi.uploadDocument(targetKbId, file);
      }
      // Google Drive 파일 가져오기
      if (hasFile && driveFiles.length > 0) {
        for (const df of driveFiles) {
          await connectorApi.importDriveFileToKb(targetKbId, df.fileId, df.fileName);
        }
      }
      // Dropbox 파일 가져오기
      if (hasFile && dropboxFiles.length > 0) {
        for (const df of dropboxFiles) {
          await connectorApi.importKbDropboxFileToKb(targetKbId, df.filePath, df.fileName);
        }
      }
      if (hasWeb || hasDatabase) await knowledgeBaseApi.sync(targetKbId);

      const msg = isEditing
        ? 'DB 설정이 수정되었습니다. 동기화를 실행하면 재인덱싱됩니다.'
        : activeSources.length > 1
        ? '지식베이스가 생성되었습니다. 각 소스 처리가 시작됩니다.'
        : hasFile
        ? '지식베이스가 생성되었습니다. 문서 처리가 시작됩니다.'
        : hasWeb
        ? '웹 크롤링이 설정되었습니다. 크롤링이 시작됩니다.'
        : 'DB 커넥터가 설정되었습니다. 데이터 동기화가 시작됩니다.';
      showToast('success', msg);
      onComplete();
    } catch {
      showToast('error', '지식베이스 생성에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  // ── 단계별 렌더 ──
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <SourceSelectStep
            kbName={kbName}
            onKbNameChange={setKbName}
            kbDescription={kbDescription}
            onKbDescriptionChange={setKbDescription}
            selectedCustomerId={kbCustomerId}
            onCustomerIdChange={setKbCustomerId}
            selectedTeamId={kbTeamId}
            onTeamIdChange={setKbTeamId}
            hasFile={hasFile}
            onHasFileChange={setHasFile}
            hasWeb={hasWeb}
            onHasWebChange={setHasWeb}
            hasDatabase={hasDatabase}
            onHasDatabaseChange={setHasDatabase}
            activeSources={activeSources}
            isUserMode={isUserMode}
            lockedSource={isSingleSourcePage ? defaultSourceType : undefined}
          />
        );
      case 2:
        return (
          <DataSourceStep
            activeSources={activeSources}
            activeTab={activeTab}
            onActiveTabChange={setActiveTab}
            hasFile={hasFile}
            files={files}
            onFilesChange={setFiles}
            isDragOver={isDragOver}
            onIsDragOverChange={setIsDragOver}
            driveFiles={driveFiles}
            onDriveFilesChange={setDriveFiles}
            dropboxFiles={dropboxFiles}
            onDropboxFilesChange={setDropboxFiles}
            hasWeb={hasWeb}
            webConfig={webConfig}
            onWebConfigChange={setWebConfig}
            hasDatabase={hasDatabase}
            dbConfig={dbConfig}
            onDbConfigChange={setDbConfig}
          />
        );
      case 3:
        return (
          <PreprocessingStep
            activeSources={activeSources}
            activeTab={activeTab}
            onActiveTabChange={setActiveTab}
            files={files}
            driveFiles={driveFiles}
            dropboxFiles={dropboxFiles}
            fileParser={fileParser}
            onFileParserChange={setFileParser}
            webPreprocessing={webPreprocessing}
            onWebPreprocessingChange={setWebPreprocessing}
            dbPreprocessing={dbPreprocessing}
            onDbPreprocessingChange={setDbPreprocessing}
          />
        );
      case 4:
        return (
          <ChunkingStep
            activeSources={activeSources}
            activeTab={activeTab}
            onActiveTabChange={setActiveTab}
            fileChunking={fileChunking}
            setFileChunking={setFileChunking}
            webChunking={webChunking}
            setWebChunking={setWebChunking}
            dbChunking={dbChunking}
            setDbChunking={setDbChunking}
          />
        );
      case 5:
        return (
          <EmbeddingStep
            embeddingModelId={embeddingModelId}
            onEmbeddingModelIdChange={setEmbeddingModelId}
            embeddingMode={embeddingMode}
            onEmbeddingModeChange={setEmbeddingMode}
            files={files}
            hasFile={hasFile}
          />
        );
      case 6:
        return <SearchConfigStep search={search} onSearchChange={setSearch} hasDatabase={hasDatabase} />;
      case 7:
        return (
          <ReviewStep
            kbName={kbName}
            kbDescription={kbDescription}
            activeSources={activeSources}
            hasFile={hasFile}
            files={files}
            driveFiles={driveFiles}
            dropboxFiles={dropboxFiles}
            fileChunking={fileChunking}
            hasWeb={hasWeb}
            webConfig={webConfig}
            webChunking={webChunking}
            hasDatabase={hasDatabase}
            dbConfig={dbConfig}
            dbChunking={dbChunking}
            embeddingModelId={embeddingModelId}
            topK={search.topK}
            similarityThreshold={search.similarityThreshold}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      closeOnEsc={false}
      dialogRef={modalRef}
      className={`pipe-modal${isDragging ? ' dragging' : ''}`}
      style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
      // 헤더 = 드래그 핸들
      headerProps={{ className: 'pipe-topbar', onMouseDown: handleMouseDown }}
      bodyClassName="pipe-body"
      // 제목 + 소스 배지 + 단계 안내 툴팁 + 지식베이스 이름
      title={
        <>
          <span className="pipe-title-text">새 지식베이스</span>
          {/* <Tooltip content={HINT_TEXTS[currentStep]} placement="bottom">
            <button type="button" className="pipe-title-info" aria-label="단계 안내">
            <InfoIcon />
            </button>
            </Tooltip> */}
          {kbName && <span className="pipe-title-name">({kbName})</span>}
            {activeSources.map((s) => (
              <Badge key={s} variant={`source-${s}`} size="md" shape='tag'>{SOURCE_LABELS[s]}</Badge>
            ))}
        </>
      }
      subHeader={
        <ol className="pipe-stepper">
          {STEPS.map((step) => {
            const status = currentStep > step.num ? 'done' : currentStep === step.num ? 'current' : 'todo';
            const StepIcon = status === 'done' ? CheckIcon : step.icon;
            return (
              <li
                key={step.num}
                className={`pipe-stepper-item is-${status}`}
                aria-current={status === 'current' ? 'step' : undefined}
              >
                <div className="pipe-stepper-track">
                  <span className="pipe-stepper-circle"><StepIcon aria-hidden="true" /></span>
                  {step.num < STEPS.length && <span className="pipe-stepper-line" />}
                </div>
                <div className="pipe-stepper-num">STEP {step.num}</div>
                <div className="pipe-stepper-label">{step.label}</div>
              </li>
            );
          })}
        </ol>
      }
      footerRight={
        <>
          <Button color="gray" appearance="outline" onClick={onClose}>닫기</Button>
          {currentStep > 1 && (
            <Button color="primary" appearance="outline" onClick={handlePrev}>← 이전</Button>
          )}
          {currentStep < 7 ? (
            <Button color="primary" onClick={handleNext} disabled={!canProceed()}>다음 →</Button>
          ) : (
            <Button color="primary" onClick={handlePublish} disabled={uploading}>
              {uploading ? '생성 중...' : '게시하기'}
            </Button>
          )}
        </>
      }
    >
      {renderStepContent()}
    </Modal>
  );
}
